import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit2, Trash2, ThumbsUp, Award, ArrowUpDown, ArrowUp, ArrowDown, Archive, ArchiveRestore, Filter } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';

export default function PolicyTableView({
  policies,
  officers,
  capabilities,
  user,
  isSeniorOfficer,
  onEdit,
  onDelete,
  onApprove,
  onInstitutionalize,
  onArchive,
  onUnarchive,
  onRetire,
  deleteConfirm
}) {
  const [sortKey, setSortKey] = useState('title');
  const [sortDir, setSortDir] = useState('asc');
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 text-indigo-400" />
      : <ArrowDown className="w-3 h-3 ml-1 text-indigo-400" />;
  };

  const statusColors = {
    draft: 'bg-slate-500/20 text-slate-400',
    pending_approval: 'bg-amber-500/20 text-amber-400',
    approved: 'bg-emerald-500/20 text-emerald-400',
    institutionalized: 'bg-indigo-500/20 text-indigo-400',
    active: 'bg-emerald-500/20 text-emerald-400',
    under_review: 'bg-amber-500/20 text-amber-400',
    retired: 'bg-red-500/20 text-red-400'
  };

  const enforceabilityColors = {
    easy: 'bg-emerald-500/20 text-emerald-400',
    moderate: 'bg-amber-500/20 text-amber-400',
    difficult: 'bg-orange-500/20 text-orange-400',
    unenforceable: 'bg-red-500/20 text-red-400'
  };

  const now = new Date();

  const filtered = useMemo(() => {
    if (!showOverdueOnly) return policies;
    return policies.filter(p => {
      if (!p.interim_for_capability_development || !p.review_date) return false;
      return new Date(p.review_date) < now;
    });
  }, [policies, showOverdueOnly]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let valA, valB;
      switch (sortKey) {
        case 'title':
          valA = (a.title || '').toLowerCase();
          valB = (b.title || '').toLowerCase();
          break;
        case 'status':
          valA = (a.status || '').toLowerCase();
          valB = (b.status || '').toLowerCase();
          break;
        case 'designer':
          const dA = officers.find(o => o.user_email === a.designed_by);
          const dB = officers.find(o => o.user_email === b.designed_by);
          valA = (dA?.callsign || a.designed_by || '').toLowerCase();
          valB = (dB?.callsign || b.designed_by || '').toLowerCase();
          break;
        case 'created':
          valA = a.designed_at ? new Date(a.designed_at).getTime() : 0;
          valB = b.designed_at ? new Date(b.designed_at).getTime() : 0;
          break;
        case 'age':
          valA = a.designed_at ? differenceInDays(now, new Date(a.designed_at)) : 0;
          valB = b.designed_at ? differenceInDays(now, new Date(b.designed_at)) : 0;
          break;
        case 'review':
          valA = a.review_date ? new Date(a.review_date).getTime() : 9999999999999;
          valB = b.review_date ? new Date(b.review_date).getTime() : 9999999999999;
          break;
        default:
          valA = '';
          valB = '';
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir, officers]);

  const overdueCount = policies.filter(p => 
    p.interim_for_capability_development && p.review_date && new Date(p.review_date) < now
  ).length;

  return (
    <div className="space-y-3">
      {/* Overdue Filter */}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={showOverdueOnly ? "default" : "outline"}
          className={`h-7 text-xs ${showOverdueOnly ? 'bg-red-600 hover:bg-red-700' : ''}`}
          onClick={() => setShowOverdueOnly(!showOverdueOnly)}
        >
          <Filter className="w-3 h-3 mr-1" />
          Show Overdue Reviews {overdueCount > 0 && `(${overdueCount})`}
        </Button>
      </div>

      <div className="border border-slate-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('title')}>
                  <span className="flex items-center">Title <SortIcon column="title" /></span>
                </TableHead>
                <TableHead className="text-slate-400">Type</TableHead>
                <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('status')}>
                  <span className="flex items-center">Status <SortIcon column="status" /></span>
                </TableHead>
                <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('review')}>
                  <span className="flex items-center">Review Date <SortIcon column="review" /></span>
                </TableHead>
                <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('age')}>
                  <span className="flex items-center">Age <SortIcon column="age" /></span>
                </TableHead>
                <TableHead className="text-slate-400">Enforceability</TableHead>
                <TableHead className="text-slate-400 cursor-pointer select-none" onClick={() => toggleSort('designer')}>
                  <span className="flex items-center">Designed By <SortIcon column="designer" /></span>
                </TableHead>
                <TableHead className="text-slate-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((policy) => {
                const designer = officers.find(o => o.user_email === policy.designed_by);
                const isBridging = policy.interim_for_capability_development;
                const ageDays = policy.designed_at ? differenceInDays(now, new Date(policy.designed_at)) : null;
                const reviewDate = policy.review_date ? new Date(policy.review_date) : null;
                const isReviewOverdue = reviewDate && reviewDate < now;

                return (
                  <TableRow key={policy.id} className="border-slate-800 hover:bg-slate-800/30">
                    <TableCell>
                      <span className="text-sm text-slate-200 font-medium">{policy.title}</span>
                    </TableCell>
                    <TableCell>
                      {isBridging ? (
                        <Badge className="bg-amber-500/20 text-amber-400 border-none text-[10px]">Bridging</Badge>
                      ) : (
                        <Badge className="bg-slate-500/20 text-slate-400 border-none text-[10px]">Standing</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[policy.status] || 'bg-slate-500/20 text-slate-400'} border-none text-xs`}>
                        {policy.status?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {reviewDate ? (
                        <span className={`text-xs ${isReviewOverdue ? 'text-red-400 font-semibold' : 'text-slate-400'}`}>
                          {format(reviewDate, 'MMM dd')}
                          {isReviewOverdue && ' ⚠️'}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-600">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {ageDays !== null ? (
                        <span className={`text-xs ${isBridging && ageDays > 90 ? 'text-red-400 font-semibold' : 'text-slate-400'}`}>
                          {ageDays}d
                        </span>
                      ) : (
                        <span className="text-xs text-slate-600">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {policy.enforceability_score ? (
                        <Badge className={`${enforceabilityColors[policy.enforceability_score] || ''} border-none text-[10px]`}>
                          {policy.enforceability_score}
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-600">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-400">
                      {designer?.callsign || policy.designed_by}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {isSeniorOfficer && policy.status === 'draft' && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-400 hover:text-emerald-300" onClick={() => onApprove(policy.id)} title="Approve">
                            <ThumbsUp className="w-3 h-3" />
                          </Button>
                        )}
                        {isSeniorOfficer && policy.status === 'approved' && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-indigo-400 hover:text-indigo-300" onClick={() => onInstitutionalize(policy.id)} title="Institutionalize">
                            <Award className="w-3 h-3" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(policy)}>
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <div className="relative">
                          <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-red-400" onClick={() => deleteConfirm.checkAndConfirm(policy.id, () => onDelete(policy.id))}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                          {deleteConfirm.isPendingConfirm(policy.id) && (
                            <span className="absolute right-0 -bottom-8 text-xs text-red-400 bg-slate-900 px-2 py-1 rounded border border-red-500/30 whitespace-nowrap z-10">
                              Click again
                            </span>
                          )}
                        </div>
                        {policy.status === 'retired' ? (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-amber-400 hover:text-amber-300" onClick={() => onUnarchive(policy.id)} title="Unretire">
                            <ArchiveRestore className="w-3 h-3" />
                          </Button>
                        ) : (
                          <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-amber-400" onClick={() => onArchive(policy.id)} title="Retire">
                            <Archive className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}